<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useNewRelic } from '../../../composables/useNewRelic';
import type { ResolvedSelfSellInModel } from '../composables/useSelfSellIn';
import AuthorCard from './AuthorCard.vue';

interface Props {
  data: ResolvedSelfSellInModel['seasonQuoteSection'];
}
defineProps<Props>();

const newRelic = useNewRelic();
const videoRef = ref<HTMLVideoElement | null>(null);

onMounted(() => {
  if (videoRef.value) {
    videoRef.value.play().catch((error) => {
      newRelic.noticeError(error, {
        component: 'SeasonQuote',
      });
    });
  }
});
</script>

<template>
  <div class="video-card">
    <div class="video-card__video-container">
      <video ref="videoRef" class="video-card__video" autoplay muted loop playsinline :poster="data.video.poster">
        <source :src="data.video.url" type="video/mp4" />
      </video>
      <div class="video-card__overlay"></div>
    </div>

    <div class="video-card__content">
      <p class="video-card__quote">
        <span class="video-card__quote-bold">{{ data.title }}</span>
        {{ ' ' }}
        <span class="video-card__quote-light">{{ data.description }}</span>
      </p>

      <AuthorCard
        :authorName="data.authorName"
        :authorTitle="data.authorTitle"
        :authorAvatarUrl="data.authorAvatarUrl" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.video-card {
  position: relative;
  width: calc(100% + 48px);
  left: -24px;
  overflow: hidden;

  &__video-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  &__video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: blur(20px);
  }

  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.5) 100%);
  }

  &__content {
    position: relative;
    z-index: 1;
    padding: 145px 50px;
    display: flex;
    flex-direction: column;
    gap: 20px;

    @include respond-to(tablet) {
      padding: 145px 250px;
    }
  }

  &__quote {
    font-size: 2.25rem;
    line-height: 1.1;
    font-weight: 400;
    margin: 0;
    text-align: left;
    letter-spacing: -0.72px;
  }

  &__quote-bold {
    color: var(--white-color);
  }

  &__quote-light {
    color: rgba(255, 255, 255, 0.5);
  }
}
</style>
